import { logisticsApi } from "@/api/apiClient";
import { useQuery } from "@tanstack/react-query";
import { dateSchema } from "@medoma/models/date-and-time";
import { patientNoteStatusSchema } from "@medoma/models/patientNotes";
import { HTTPError } from "ky";
import { z } from "zod";

// The reader in Care is the patient, so this carries only what the patient is shown:
// the AI-rewritten text, who approved it and when. The structured source note the
// rewrite is based on stays out, and the accepted status is a literal so a note that
// has not been approved fails to parse rather than reaching the patient.
export const acceptedPatientNoteSchema = z.object({
  status: z.literal(patientNoteStatusSchema.enum.accepted),
  acceptedBy: z.string().nullish(),
  convertedText: z.string(),
  conversionTimeStamp: dateSchema.nullish(),
});

const fetchAcceptedPatientNote = async () => {
  try {
    return await logisticsApi
      .get("conversion/patient-note/latest")
      .json(acceptedPatientNoteSchema);
  } catch (error) {
    // In case of error, 404 Not Found is expected when there is no patient note, which is a common scenario.
    if (error instanceof HTTPError && error.response.status === 404) {
      return null;
    }
    throw error;
  }
};

const patientNoteKeys = {
  all: ["conversion"] as const,
  lists: () => [...patientNoteKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...patientNoteKeys.lists(), filters] as const,
  detail: ["details"] as const,
};

export const useAcceptedPatientNote = () => {
  return useQuery({
    queryKey: patientNoteKeys.detail,
    queryFn: fetchAcceptedPatientNote,
    staleTime: 5000,
  });
};
